import React, { useState, useRef, useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Search, ArrowRight, Loader2 } from "lucide-react";
import wtsappImage from "../../assets/images/support/whatsapp.png";
import emailImage from "../../assets/images/support/email.png";
import faqImage from "../../assets/images/support/faqs.png";
import chatImage from "../../assets/images/support/chat.png";
import Header from "../../layouts/Header";
import { useNavigate } from "react-router-dom";
import {
  useFeatureFlagsByCategory,
  useFaqSearch,
} from "../../hooks/query/queries";

const SEARCH_DEBOUNCE_MS = 400;

const SupportMenu = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchRef = useRef(null);
  const suggestionsRef = useRef(null);
  const { data: featureFlags } = useFeatureFlagsByCategory("supportPage");
  const searchBarEnabled = featureFlags?.searchBar;
  const whatsappEnabled = featureFlags?.whatsapp;
  const emailEnabled = featureFlags?.email;
  const faqsEnabled = featureFlags?.faqs;
  const chatEnabled = featureFlags?.chat;

  const { data: searchData, isLoading: searchLoading } = useFaqSearch(
    debouncedQuery,
    { limit: 10 }
  );
  const searchResults = searchData?.results ?? [];

  useEffect(() => {
    if (!searchQuery.trim()) {
      setDebouncedQuery("");
      return;
    }
    const t = setTimeout(
      () => setDebouncedQuery(searchQuery.trim()),
      SEARCH_DEBOUNCE_MS
    );
    return () => clearTimeout(t);
  }, [searchQuery]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target) &&
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <>
      <Header />
      <div className="px-4 py-6 min-h-screen bg-[#F8F8F8]">
        <div className="w-full lg:min-h-screen min-h-[600px] flex flex-col items-center px-6 lg:py-40 py-6 lg:bg-transparent bg-white overflow-x-hidden rounded-3xl">
          <div className="text-center lg:mb-10 mb-6">
            <h1 className="lg:text-[32px] text-[20px] font-semibold text-[#33125F]">
              Hello, how can we help?
            </h1>
          </div>
          <div className="lg:w-auto w-full max-w-[844px] relative">
            <div className="flex items-center justify-between gap-3 lg:mb-14 mb-6">
              {searchBarEnabled && (
                <div
                  className="flex-1 lg:min-w-[600px] min-w-full relative"
                  ref={searchRef}
                >
                  <Input
                    type="text"
                    placeholder="Search"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setShowSuggestions(true);
                    }}
                    onFocus={() => setShowSuggestions(true)}
                    className="w-full h-[56px] pl-12 pr-4 rounded-full text-[#6D6584] text-[14px] border border-[#EBE9EF] hover:border-brand-light focus:border-brand-light focus-visible:border-brand-light active:border-brand-light focus:ring focus:ring-brand-light focus-visible:ring focus-visible:ring-brand-light outline-none focus:outline-none focus-visible:outline-none active:outline-none transition-colors"
                  />
                  {searchLoading ? (
                    <Loader2 className="absolute left-[1.5rem] top-[1.3rem] w-[15px] h-[15px] text-[#6D6584] animate-spin" />
                  ) : (
                    <Search className="absolute left-[1.5rem] top-[1.3rem] w-[15px] h-[15px] text-[#6D6584] transition-transform duration-300 ease-in-out hover:scale-110" />
                  )}

                  {searchQuery.trim() && showSuggestions && (
                    <div
                      ref={suggestionsRef}
                      className="absolute top-[66px] left-0 right-0 mt-2 bg-white rounded-2xl border border-[#EBE9EF] z-50 shadow-lg"
                    >
                      {searchQuery.trim() ? (
                        <>
                          {searchLoading && searchResults.length === 0 ? (
                            <div className="p-6 text-center text-[#6D6584] text-[14px]">
                              Searching...
                            </div>
                          ) : searchResults.length > 0 ? (
                            <div className="p-4 space-y-2 max-h-[320px] overflow-y-auto">
                              {searchResults.map((item) => (
                                <div
                                  key={item.id}
                                  onClick={() => {
                                    navigate("/support/faqs", {
                                      state: { searchQuery: item.question },
                                    });
                                    setShowSuggestions(false);
                                  }}
                                  className="px-6 py-4 rounded-xl border border-[#EBE9EF] hover:bg-[#F8F8F8] cursor-pointer transition-colors"
                                >
                                  <p className="text-[16px] text-[#423E51] font-semibold">
                                    {item.question}
                                  </p>
                                  {item.category && (
                                    <p className="text-[12px] text-[#6D6584] mt-1">
                                      {item.category}
                                    </p>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="py-12 text-center text-[#6D6584] text-[20px] min-h-[200px]">
                              No results found
                            </div>
                          )}
                        </>
                      ) : null}
                      <div className="px-4 pb-4 flex justify-center border-t border-[#EBE9EF] pt-4">
                        <button
                          onClick={() => navigate("/support/chat")}
                          className="px-6 py-3 rounded-full text-white text-[14px] font-semibold bg-gradient-to-r from-[#57249C] to-[#EB3366] hover:opacity-90 transition-opacity min-w-[306px]"
                        >
                          Start new chat
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex justify-center lg:flex-row flex-col lg:gap-6 gap-3">
              {whatsappEnabled && (
                <Card
                  className="lg:w-60 hover:shadow-lg hover:scale-105 transition-transform duration-300 ease-in-out hover:cursor-pointer !border-[#EBE9EF] rounded-2xl lg:h-fit lg:min-h-[250px] h-[72px] flex items-center justify-center lg:flex-col flex-row"
                  onClick={() => {
                    navigate("/support/whatsapp");
                  }}
                >
                  <CardHeader className="flex lg:flex-col flex-row items-center lg:justify-center justify-between w-full lg:space-y-1.5 space-y-0 lg:px-6 px-4 lg:py-6 py-0">
                    <div className="flex items-center justify-center lg:flex-col flex-row gap-2">
                      <img
                        src={wtsappImage}
                        alt="WhatsApp"
                        className="lg:w-[56px] lg:h-[56px] w-[40px] h-[40px] lg:mb-4 mb-0 transition-transform duration-300 ease-in-out hover:scale-110"
                      />
                      <CardTitle>
                        <span className="lg:text-[20px] text-[14px] font-semibold text-[#423E51] lg:mb-0 mb-1.5 lg:inline block">
                          WhatsApp
                        </span>
                        <span className="block lg:hidden lg:text-[14px] text-[12px] font-normal text-[#6D6584]">
                          Chat to one of our agents now
                        </span>
                      </CardTitle>
                    </div>
                    <div className="border-rounded rounded-full p-2 border lg:hidden block">
                      <ArrowRight
                        size={20}
                        className="text-[#6D6584] font-normal"
                        onClick={() => {
                          navigate("/support/whatsapp");
                        }}
                      />
                    </div>
                  </CardHeader>
                  <CardContent className="text-center text-[#6D6584] text-[16px] p-4 pt-0 pb-6 lg:block hidden">
                    Start a WhatsApp chat with one of our agents now
                  </CardContent>
                </Card>
              )}

              {emailEnabled && (
                <Card
                  className="lg:w-60 hover:shadow-lg hover:scale-105 transition-transform duration-300 ease-in-out hover:cursor-pointer !border-[#EBE9EF] rounded-2xl lg:h-fit lg:min-h-[250px] h-[72px] flex items-center justify-center lg:flex-col flex-row"
                  onClick={() => {
                    navigate("/support/email");
                  }}
                >
                  <CardHeader className="flex lg:flex-col flex-row items-center lg:justify-center justify-between w-full lg:space-y-1.5 space-y-0 lg:px-6 px-4 lg:py-6 py-0">
                    <div className="flex items-center justify-center lg:flex-col flex-row gap-2">
                      <img
                        src={emailImage}
                        alt="Email"
                        className="lg:w-[56px] lg:h-[56px] w-[40px] h-[40px] lg:mb-4 mb-0 transition-transform duration-300 ease-in-out hover:scale-110"
                      />
                      <CardTitle>
                        <span className="lg:text-[20px] text-[14px] font-semibold text-[#423E51] lg:mb-0 mb-0 lg:inline block">
                          Get in touch
                        </span>
                      </CardTitle>
                    </div>
                    <div className="border-rounded rounded-full p-2 border lg:hidden block">
                      <ArrowRight
                        size={20}
                        className="text-[#6D6584] font-normal"
                        onClick={() => {
                          navigate("/support/email");
                        }}
                      />
                    </div>
                  </CardHeader>
                  <CardContent className="text-center text-[#6D6584] text-[16px] p-4 pt-0 pb-6 lg:block hidden">
                    Share your questions and feedback with our support team
                  </CardContent>
                </Card>
              )}

              {faqsEnabled && (
                <Card
                  className="lg:w-60 hover:shadow-lg hover:scale-105 transition-transform duration-300 ease-in-out hover:cursor-pointer !border-[#EBE9EF] rounded-2xl lg:h-fit lg:min-h-[250px] h-[72px] flex items-center justify-center lg:flex-col flex-row"
                  onClick={() => {
                    navigate("/support/faqs");
                  }}
                >
                  <CardHeader className="flex lg:flex-col flex-row items-center lg:justify-center justify-between w-full lg:space-y-1.5 space-y-0 lg:px-6 px-4 lg:py-6 py-0">
                    <div className="flex items-center justify-center lg:flex-col flex-row gap-2">
                      <img
                        src={faqImage}
                        alt="FAQs"
                        className="lg:w-[56px] lg:h-[56px] w-[40px] h-[40px] lg:mb-4 mb-0 transition-transform duration-300 ease-in-out hover:scale-110"
                      />
                      <CardTitle>
                        <span className="lg:text-[20px] text-[14px] font-semibold text-[#423E51] lg:mb-0 mb-0 lg:inline block">
                          FAQs
                        </span>
                      </CardTitle>
                    </div>
                    <div className="border-rounded rounded-full p-2 border lg:hidden block">
                      <ArrowRight
                        size={20}
                        className="text-[#6D6584] font-normal"
                        onClick={() => {
                          navigate("/support/faqs");
                        }}
                      />
                    </div>
                  </CardHeader>
                  <CardContent className="text-center text-[#6D6584] text-[16px] p-4 pt-0 pb-6 lg:block hidden">
                    Get answers to frequently asked questions
                  </CardContent>
                </Card>
              )}

              {chatEnabled && (
                <Card
                  className="lg:w-60 hover:shadow-lg hover:scale-105 transition-transform duration-300 ease-in-out hover:cursor-pointer !border-[#EBE9EF] rounded-2xl lg:h-fit lg:min-h-[250px] h-[72px] flex items-center justify-center lg:flex-col flex-row"
                  onClick={() => {
                    navigate("/support/chat");
                  }}
                >
                  <CardHeader className="flex lg:flex-col flex-row items-center lg:justify-center justify-between w-full lg:space-y-1.5 space-y-0 lg:px-6 px-4 lg:py-6 py-0">
                    <div className="flex items-center justify-center lg:flex-col flex-row gap-2">
                      <img
                        src={chatImage}
                        alt="Chat"
                        className="lg:w-[56px] lg:h-[56px] w-[40px] h-[40px] lg:mb-4 mb-0 transition-transform duration-300 ease-in-out hover:scale-110"
                      />
                      <CardTitle>
                        <span className="lg:text-[20px] text-[14px] font-semibold text-[#423E51] lg:mb-0 mb-0 lg:inline block">
                          Open Chat
                        </span>
                      </CardTitle>
                    </div>
                    <div className="border-rounded rounded-full p-2 border lg:hidden block">
                      <ArrowRight
                        size={20}
                        className="text-[#6D6584] font-normal"
                        onClick={() => {
                          navigate("/support/chat");
                        }}
                      />
                    </div>
                  </CardHeader>
                  <CardContent className="text-center text-[#6D6584] text-[16px] p-4 pt-0 pb-6 lg:block hidden">
                    Start a chat with our support team
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SupportMenu;
